import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Image, 
  Send, 
  RefreshCw, 
  ExternalLink,
  Filter,
  Grid,
  List,
  Clock,
  CheckCircle
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';

interface Nft {
  id: string;
  tokenId: string;
  contractAddress: string;
  name: string;
  description?: string;
  imageUrl?: string;
  collection: string;
  network: string;
  standard: 'ERC721' | 'ERC1155';
  attributes?: { trait_type: string; value: string }[];
  acquiredAt: string;
}

interface NftTransfer {
  id: string;
  nftId: string;
  nftName: string;
  fromAddress: string;
  toAddress: string;
  txHash: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
}

export default function NftGallery() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'gallery' | 'history'>('gallery');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedNft, setSelectedNft] = useState<Nft | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [networkFilter, setNetworkFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');


  // Fetch NFTs
  const { data: nfts, isLoading: loadingNfts } = useQuery({
    queryKey: ['nfts', networkFilter, collectionFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (networkFilter !== 'all') params.append('network', networkFilter);
      if (collectionFilter !== 'all') params.append('collection', collectionFilter);
      const response = await api.get(`/crypto/nfts?${params.toString()}`);
      return response.data as Nft[];
    },
  });

  // Fetch transfer history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['nft-history'],
    queryFn: async () => {
      const response = await api.get('/crypto/nfts/history');
      return response.data as NftTransfer[];
    },
    enabled: activeTab === 'history',
  });

  // Sync NFTs mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/crypto/nfts/sync');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfts'] });
    },
  });

  // Transfer NFT mutation
  const transferMutation = useMutation({
    mutationFn: async (data: { nftId: string; toAddress: string }) => {
      const response = await api.post(`/crypto/nfts/${data.nftId}/transfer`, {
        toAddress: data.toAddress,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nfts'] });
      queryClient.invalidateQueries({ queryKey: ['nft-history'] });
      setShowTransferModal(false);
      setRecipientAddress('');
      setSelectedNft(null);
    },
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleTransfer = () => {
    if (selectedNft && recipientAddress) {
      transferMutation.mutate({
        nftId: selectedNft.id,
        toAddress: recipientAddress,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      PENDING: 'Pendente',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  // Get unique collections and networks for filters
  const collections = [...new Set(nfts?.map(n => n.collection) || [])];
  const networks = [...new Set(nfts?.map(n => n.network) || [])];

  return (
    <PageContainer
      title="Galeria de NFTs"
      description="Visualize e gerencie seus NFTs"
      icon={Image}
      actions={
        <Button
          variant="secondary"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sincronizar
        </Button>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'gallery' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('gallery')}
        >
          <Grid className="w-4 h-4 mr-2" />
          Galeria
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('history')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
      </div>

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <Select
              value={networkFilter}
              onChange={(e) => setNetworkFilter(e.target.value)}
              className="w-40"
            >
              <option value="all">Todas as Redes</option>
              {networks.map(network => (
                <option key={network} value={network}>{network}</option>
              ))}
            </Select>
            <Select
              value={collectionFilter}
              onChange={(e) => setCollectionFilter(e.target.value)}
              className="w-48"
            >
              <option value="all">Todas as Coleções</option>
              {collections.map(collection => (
                <option key={collection} value={collection}>{collection}</option>
              ))}
            </Select>
            <div className="flex gap-1 ml-auto">
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* NFT Grid/List */}
          {loadingNfts ? (
            <div className="text-center py-8 text-surface-400">Carregando...</div>
          ) : nfts && nfts.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {nfts.map((nft) => (
                  <Card
                    key={nft.id}
                    className="cursor-pointer hover:border-primary-500/50 transition-colors overflow-hidden"
                    onClick={() => {
                      setSelectedNft(nft);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="aspect-square bg-surface-800 relative">
                      {nft.imageUrl ? (
                        <img
                          src={nft.imageUrl}
                          alt={nft.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-surface-600" />
                        </div>
                      )}
                      <Badge className="absolute top-2 right-2" variant="info">
                        {nft.network}
                      </Badge>
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-surface-100 truncate">{nft.name}</h4>
                      <p className="text-sm text-surface-400 truncate">{nft.collection}</p>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <div className="divide-y divide-surface-700">
                  {nfts.map((nft) => (
                    <div
                      key={nft.id}
                      className="flex items-center gap-4 p-4 hover:bg-surface-800/50 cursor-pointer"
                      onClick={() => {
                        setSelectedNft(nft);
                        setShowDetailModal(true);
                      }}
                    >
                      <div className="w-16 h-16 rounded-lg bg-surface-800 overflow-hidden flex-shrink-0">
                        {nft.imageUrl ? (
                          <img src={nft.imageUrl} alt={nft.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-6 h-6 text-surface-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-surface-100">{nft.name}</h4>
                        <p className="text-sm text-surface-400">{nft.collection}</p>
                      </div>
                      <Badge variant="info">{nft.network}</Badge>
                      <Badge variant="default">{nft.standard}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )
          ) : (
            <Card>
              <div className="p-8 text-center text-surface-400">
                <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum NFT encontrado</p>
                <Button
                  variant="secondary"
                  className="mt-4"
                  onClick={() => syncMutation.mutate()}
                >
                  Sincronizar Carteiras
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Histórico de Transferências</h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((transfer) => (
                  <div key={transfer.id} className="flex items-center justify-between p-4 bg-surface-800 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                        <Send className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">{transfer.nftName}</p>
                        <p className="text-sm text-surface-400">
                          Para: {truncateAddress(transfer.toAddress)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-surface-400">{formatDate(transfer.createdAt)}</p>
                      {getStatusBadge(transfer.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhuma transferência encontrada
              </div>
            )}
          </div>
        </Card>
      )}

      {/* NFT Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedNft(null);
        }}
        title={selectedNft?.name || 'Detalhes do NFT'}
        size="lg"
      >
        {selectedNft && (
          <div className="space-y-4">
            <div className="aspect-square max-h-80 bg-surface-800 rounded-lg overflow-hidden mx-auto">
              {selectedNft.imageUrl ? (
                <img src={selectedNft.imageUrl} alt={selectedNft.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-16 h-16 text-surface-600" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">Coleção</span>
                <span className="text-surface-100">{selectedNft.collection}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Rede</span>
                <span className="text-surface-100">{selectedNft.network}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Padrão</span>
                <span className="text-surface-100">{selectedNft.standard}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Token ID</span>
                <span className="text-surface-100 font-mono text-sm">{selectedNft.tokenId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Contrato</span>
                <span className="text-surface-100 font-mono text-sm">{truncateAddress(selectedNft.contractAddress)}</span>
              </div>
            </div>

            {selectedNft.attributes && selectedNft.attributes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-2">Atributos</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedNft.attributes.map((attr, idx) => (
                    <div key={idx} className="p-2 bg-surface-800 rounded text-center">
                      <p className="text-xs text-surface-400">{attr.trait_type}</p>
                      <p className="text-sm text-surface-100">{attr.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedNft.description && (
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-2">Descrição</h4>
                <p className="text-sm text-surface-400">{selectedNft.description}</p>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowDetailModal(false)}
                className="flex-1"
              >
                Fechar
              </Button>
              <Button
                onClick={() => {
                  setShowDetailModal(false);
                  setShowTransferModal(true);
                }}
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                Transferir
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Transfer Modal */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setRecipientAddress('');
        }}
        title="Transferir NFT"
      >
        {selectedNft && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-surface-800 rounded-lg">
              <div className="w-16 h-16 rounded-lg bg-surface-700 overflow-hidden">
                {selectedNft.imageUrl ? (
                  <img src={selectedNft.imageUrl} alt={selectedNft.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-surface-600" />
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-medium text-surface-100">{selectedNft.name}</h4>
                <p className="text-sm text-surface-400">{selectedNft.collection}</p>
              </div>
            </div>

            <Input
              label="Endereço do Destinatário"
              placeholder="0x..."
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
            />

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTransferModal(false);
                  setRecipientAddress('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleTransfer}
                disabled={!recipientAddress || transferMutation.isPending}
                className="flex-1"
              >
                {transferMutation.isPending ? 'Transferindo...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
